import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { SettingsAvailabilityEnum } from '@hunch/frontend-shared/enums';
import { BehaviorSubject, finalize, map, Observable, of, switchMap, tap } from 'rxjs';

import { UriConfig } from '@app/app.config';
import { PageSettingEnum } from '@enums/PageSettingEnum';
import { ConditionalSettingsConfigItemModel, SettingsConfigItemModel } from '@models/SettingsConfigItemModel';
import { UserDetailsModel } from '@models/UserDetailsModel';

import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { LocalizationService } from './localization.service';
import { ProfileService } from './profile.service';

@Injectable({
  providedIn: 'root'
})
export class SaasSettingsService {
  protected http = inject(HttpClient);
  private uriConfig = inject(UriConfig);
  private localStorageService = inject(LocalStorageService);
  private authService = inject(AuthService);
  private profileService = inject(ProfileService);
  private localizationService = inject(LocalizationService);

  settingConfigArray$ = new BehaviorSubject<SettingsConfigItemModel[]>([]);

  appInitialized = signal<boolean>(false);

  pageVisibilitySetting$ = new BehaviorSubject<{ [key: string]: boolean }>({});

  isAutoResultingEnabled$ = new BehaviorSubject(false);

  manualAutoResultingProcessing$ = new BehaviorSubject(false);

  isPredefinedAutoResulting$ = new BehaviorSubject(false);

  isConnect3OnboardingEnabled$ = new BehaviorSubject(false);

  isPredefineQuestionResultAllowedSignal = signal<boolean>(false);

  isPtbOnboardingEnabledSignal = signal<boolean>(false);

  minimumPrizeAmount = signal<number>(0);

  getSettingsHash() {
    return this.http.get(this.uriConfig.settings + '/check-modifying/vendor');
  }

  getSettingsList(
    userId,
    filter?: { availability: Array<SettingsAvailabilityEnum> }
  ): Observable<SettingsConfigItemModel[]> {
    let params = new HttpParams();
    if (filter?.availability) {
      filter.availability.forEach(availability => {
        params = params.append('availability', availability);
      });
    }

    return this.http.get<SettingsConfigItemModel[]>(this.uriConfig.settings + `/${userId}`, { params });
  }

  updateSetting(setting: SettingsConfigItemModel | ConditionalSettingsConfigItemModel) {
    return this.http.patch(this.uriConfig.settings + `/${setting.id}`, setting);
  }

  updateSettingDefault(setting: SettingsConfigItemModel | ConditionalSettingsConfigItemModel, id: number) {
    return this.http.patch(this.uriConfig.settings + `/default/${id}`, setting);
  }

  createSetting(setting: SettingsConfigItemModel) {
    return this.http.post(this.uriConfig.settings, { items: [setting] });
  }

  loadConfig(): Observable<SettingsConfigItemModel[] | object> {
    this.appInitialized.set(true);

    return this.authService.getCurrentUser().pipe(
      switchMap(user => {
        if (!user) {
          this.appInitialized.set(false);
          return of({});
        }

        const role = this.authService.getCurrentRole();

        if (!role) {
          this.appInitialized.set(false);
          return of({});
        }

        if (role === 'ADMIN') {
          this.appInitialized.set(false);
          return this.profileService.fetchAdminInfo().pipe(
            switchMap((user: UserDetailsModel) => {
              this.profileService.currentUser$.next(user);
              this.localStorageService.removeTenantType();
              return of({});
            })
          );
        }

        return this.profileService.fetchUserInfo().pipe(
          switchMap((currentUser: UserDetailsModel) => {
            this.profileService.currentUser$.next(currentUser);
            this.localStorageService.setTenantType(currentUser?.tenantType);
            if (currentUser) {
              this.localizationService.fetchVendorLocale();
            }
            return this.getSettingsHash().pipe(
              map((hashResponse: { hash: string }) => {
                return [hashResponse.hash, currentUser.id];
              })
            );
          }),
          switchMap(([hashResponse, id]: [string, number]) => {
            const hash = this.localStorageService.getHash();
            const savedSettingConfig = this.localStorageService.getSettingsConfig();
            if (hashResponse === hash && savedSettingConfig) {
              this.settingConfigArray$.next(savedSettingConfig);
              this.pageVisibilitySetting$.next(this.getPageVisibilitySettings(savedSettingConfig));
              return of(savedSettingConfig);
            } else {
              this.localStorageService.setHash(hashResponse);
              return this.getSettingsList(id, {
                availability: [SettingsAvailabilityEnum.VD, SettingsAvailabilityEnum.BOTH]
              }).pipe(
                switchMap((config: SettingsConfigItemModel[]) => {
                  this.localStorageService.setSettingsConfig(config);
                  this.pageVisibilitySetting$.next(this.getPageVisibilitySettings(config));
                  this.settingConfigArray$.next(config);
                  return of(config);
                })
              );
            }
          }),
          tap(config => {
            this.isAutoResultingEnabled$.next(this.isAutoResultingEnabled(config));
            this.manualAutoResultingProcessing$.next(this.isManualAutoResultingEnabled(config));
            this.isPredefinedAutoResulting$.next(this.isPredefinedAutoResultingEnabled(config));
            this.isPredefineQuestionResultAllowedSignal.set(this.isPredefineQuestionResultAllowed(config));
            this.isConnect3OnboardingEnabled$.next(this.isConnect3OnboardingEnabled(config));
            this.isPtbOnboardingEnabledSignal.set(this.isPtbOnboardingEnabled(config));
            this.minimumPrizeAmount.set(this.getMinimumPrizeAmount(config));
          }),
          finalize(() => this.appInitialized.set(false))
        );
      })
    );
  }

  updateSettingState(): Observable<void> {
    const currentUser = this.profileService.currentUser$.value;

    if (!currentUser?.id) {
      return of(void 0);
    }

    return this.getSettingsList(currentUser.id, {
      availability: [SettingsAvailabilityEnum.VD, SettingsAvailabilityEnum.BOTH]
    }).pipe(
      tap((freshConfig: SettingsConfigItemModel[]) => {
        this.settingConfigArray$.next(freshConfig);
        this.pageVisibilitySetting$.next(this.getPageVisibilitySettings(freshConfig));
        this.isAutoResultingEnabled$.next(this.isAutoResultingEnabled(freshConfig));
        this.manualAutoResultingProcessing$.next(this.isManualAutoResultingEnabled(freshConfig));
        this.isPredefinedAutoResulting$.next(this.isPredefinedAutoResultingEnabled(freshConfig));
        this.isPredefineQuestionResultAllowedSignal.set(this.isPredefineQuestionResultAllowed(freshConfig));
        this.isConnect3OnboardingEnabled$.next(this.isConnect3OnboardingEnabled(freshConfig));
        this.isPtbOnboardingEnabledSignal.set(this.isPtbOnboardingEnabled(freshConfig));
        this.minimumPrizeAmount.set(this.getMinimumPrizeAmount(freshConfig));

        this.localStorageService.setSettingsConfig(freshConfig);

        // Invalidate hash to force fresh fetch on next app load
        this.localStorageService.setHash(null);
      }),
      map(() => void 0)
    );
  }

  getPageVisibilitySettings(config) {
    if (!config || !config.length) return {};
    const pageVisibility: { [key: string]: boolean } = {};
    Object.values(PageSettingEnum).forEach(settingName => {
      pageVisibility[settingName] = config.find(configItem => configItem.key === settingName)?.value === 'true';
    });
    return pageVisibility;
  }

  getCurrencyAfterPriceSettings() {
    const config = this.localStorageService.getSettingsConfig();
    if (!config || !config.length) return null;

    return config.find(configItem => configItem.key === 'currency_after_prize_amount');
  }

  getConnect3OnboardingSetting() {
    const config = this.localStorageService.getSettingsConfig() || this.settingConfigArray$.value;
    if (!config || !config.length) return null;

    return config.find(configItem => configItem.key === 'is_connect3_onboarding_enabled');
  }

  getPtbOnboardingSetting() {
    const config = this.localStorageService.getSettingsConfig() || this.settingConfigArray$.value;
    if (!config || !config.length) return null;

    return config.find(configItem => configItem.key === 'is_ptb_onboarding_enabled');
  }

  private isAutoResultingEnabled(config) {
    return config?.find(configItem => configItem.key === 'is_auto_resulting_enabled')?.value === 'true';
  }

  private isManualAutoResultingEnabled(config) {
    return config?.find(configItem => configItem.key === 'is_auto_resulting_verification_enabled')?.value === 'true';
  }

  private isPredefinedAutoResultingEnabled(config) {
    return config?.find(configItem => configItem.key === 'is_predefined_auto_resulting_enabled')?.value === 'true';
  }

  private isPredefineQuestionResultAllowed(config) {
    return config?.find(configItem => configItem.key === 'predefine_correct_answer')?.value === 'true';
  }

  private isConnect3OnboardingEnabled(config) {
    return config?.find(configItem => configItem.key === 'is_connect3_onboarding_enabled')?.value === 'true';
  }

  private isPtbOnboardingEnabled(config): boolean {
    return config?.find(configItem => configItem.key === 'is_ptb_onboarding_enabled')?.value === 'true';
  }

  private getMinimumPrizeAmount(config): number {
    return config?.find(configItem => configItem.key === 'minimal_prize_amount')?.value;
  }
}
